import { type ReactElement, useState } from 'react';
import { useTranslation } from '@/shared/i18n/use-translation';
import { AlertDialog } from '@/shared/ui/alert-dialog/alert-dialog';
import { AlertDialogAction } from '@/shared/ui/alert-dialog/alert-dialog-action';
import { AlertDialogCancel } from '@/shared/ui/alert-dialog/alert-dialog-cancel';
import { AlertDialogContent } from '@/shared/ui/alert-dialog/alert-dialog-content';
import { AlertDialogDescription } from '@/shared/ui/alert-dialog/alert-dialog-description';
import { AlertDialogFooter } from '@/shared/ui/alert-dialog/alert-dialog-footer';
import { AlertDialogHeader } from '@/shared/ui/alert-dialog/alert-dialog-header';
import { AlertDialogTitle } from '@/shared/ui/alert-dialog/alert-dialog-title';
import { AlertDialogTrigger } from '@/shared/ui/alert-dialog/alert-dialog-trigger';

type ConfirmButtonProps = {
  readonly trigger: ReactElement;
  readonly title: string;
  readonly description?: string;
  readonly confirmLabel?: string;
  readonly destructive?: boolean;
  readonly onConfirm: () => void | Promise<void>;
};

export function ConfirmButton({
  trigger,
  title,
  description,
  confirmLabel,
  destructive = true,
  onConfirm,
}: ConfirmButtonProps) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  async function confirm() {
    await onConfirm();
    setOpen(false);
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger render={trigger} />
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('Cancel')}</AlertDialogCancel>
          <AlertDialogAction variant={destructive ? 'destructive' : 'default'} onClick={() => void confirm()}>
            {confirmLabel ?? t('Confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
